import { useCallback, useEffect, useMemo, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"

import ButtonTooltip from "components/Common/ButtonTooltip"
import { CanView } from "components/Common/CanView"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { ACCESS } from "constants/accessPermissions"
import { useEditVehicleImporter } from "hooks/useVehicleImporter"
import { useToastMessage } from "hooks/useToastMessage"

const makeEmptyEntry = subFields =>
  subFields.reduce((acc, sf) => {
    acc[sf.name] = ""
    return acc
  }, {})

const getOptionLabel = (options, value) => {
  const normalizedValue =
    value === null || value === undefined ? "" : String(value)
  const matchedOption = (options || []).find(
    option => String(option?.value ?? "") === normalizedValue
  )

  return matchedOption?.label || ""
}

const shouldShowField = (field, values) => {
  if (field.showWhen) {
    return field.showWhen(values)
  }

  return true
}

const buildSchema = fields => {
  const shape = {}

  fields.forEach(field => {
    let schema

    switch (field.type) {
      case "email":
        schema = Yup.string().nullable().email("Invalid email")
        break
      case "number":
        schema = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value
          )
          .nullable()
        break
      case "json":
        schema = Yup.string()
          .nullable()
          .test("json", "Invalid JSON format", value => {
            if (!value || !value.trim()) return true
            try {
              JSON.parse(value)
              return true
            } catch {
              return false
            }
          })
        break
      case "checkbox":
        schema = Yup.boolean()
        break
      case "repeater":
        schema = Yup.array().of(Yup.object()).nullable()
        break
      default:
        schema = Yup.string().nullable()
        break
    }

    if (field.required) {
      schema = schema.required(
        field.requiredMessage || `${field.label} is required`
      )
    }

    shape[field.name] = schema
  })

  return Yup.object(shape)
}

const buildInitialValues = fields =>
  fields.reduce((acc, field) => {
    const value = field.value

    if (field.type === "json") {
      acc[field.name] = value ? JSON.stringify(value, null, 2) : ""
    } else if (field.type === "select" || field.type === "creatable") {
      acc[field.name] =
        value === null || value === undefined ? "" : String(value)
    } else if (field.type === "checkbox") {
      acc[field.name] = Boolean(value)
    } else if (field.type === "repeater") {
      const arr = Array.isArray(value) ? value : []
      acc[field.name] = arr.length > 0 ? arr : []
    } else {
      acc[field.name] = value ?? ""
    }

    return acc
  }, {})

const normalizePayload = fields => values => {
  const payload = {}

  fields.forEach(field => {
    const rawValue = values[field.name]

    switch (field.type) {
      case "json":
        payload[field.name] =
          rawValue && rawValue.trim() ? JSON.parse(rawValue) : null
        break
      case "number":
        payload[field.name] =
          rawValue === "" || rawValue === null ? null : Number(rawValue)
        break
      case "checkbox":
        payload[field.name] = Boolean(rawValue)
        break
      case "creatable":
        payload[field.name] =
          rawValue === "" || rawValue === null ? null : rawValue
        break
      case "repeater": {
        const arr = Array.isArray(rawValue) ? rawValue : []
        const filtered = arr.filter(entry =>
          Object.values(entry).some(
            v => v !== null && v !== undefined && v !== ""
          )
        )
        payload[field.name] = filtered.length > 0 ? filtered : null
        break
      }
      default:
        payload[field.name] = rawValue === "" ? null : rawValue
        break
    }
  })

  return payload
}

const RepeaterField = ({ field, values, setFieldValue }) => {
  const entries = Array.isArray(values) ? values : []

  const addEntry = () => {
    setFieldValue(field.name, [...entries, makeEmptyEntry(field.subFields)])
  }

  const removeEntry = index => {
    setFieldValue(
      field.name,
      entries.filter((_, i) => i !== index)
    )
  }

  const updateEntry = (index, subName, val) => {
    const updated = entries.map((entry, i) =>
      i === index ? { ...entry, [subName]: val } : entry
    )
    setFieldValue(field.name, updated)
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Label className="mb-0 fw-semibold">{field.label}</Label>
        <Button
          color="success"
          size="sm"
          outline
          type="button"
          onClick={addEntry}
        >
          <i className="bx bx-plus me-1"></i>Add
        </Button>
      </div>

      {entries.length === 0 && (
        <p className="text-muted small mb-0">
          No entries yet. Click "Add" to add one.
        </p>
      )}

      {entries.map((entry, index) => (
        <div
          key={index}
          className="border rounded p-3 mb-3 position-relative bg-light"
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted fw-semibold">#{index + 1}</small>
            <Button
              color="danger"
              size="sm"
              outline
              type="button"
              onClick={() => removeEntry(index)}
            >
              <i className="bx bx-trash"></i>
            </Button>
          </div>
          <Row>
            {field.subFields.map(sf => (
              <Col md={sf.col ?? 6} key={sf.name} className="mb-2">
                <Label
                  htmlFor={`${field.name}.${index}.${sf.name}`}
                  className="small"
                >
                  {sf.label}
                </Label>
                <Input
                  id={`${field.name}.${index}.${sf.name}`}
                  type={sf.type === "textarea" ? "textarea" : "text"}
                  bsSize="sm"
                  rows={sf.type === "textarea" ? sf.rows || 2 : undefined}
                  value={entry[sf.name] ?? ""}
                  onChange={e => updateEntry(index, sf.name, e.target.value)}
                />
                {sf.helpText ? (
                  <small className="text-muted d-block mt-1">
                    {sf.helpText}
                  </small>
                ) : null}
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </div>
  )
}

export default function ImporterSectionUpdate({
  importer,
  title,
  btnId,
  btnName,
  btnStyle,
  fields = [],
  transformPayload,
  onSaved,
}) {
  const [modal, setModal] = useState(false)
  const { mutateAsync: editImporter, isPending } = useEditVehicleImporter()
  const { handleResponse, handleError } = useToastMessage()

  const validationSchema = useMemo(() => buildSchema(fields), [fields])
  const initialValues = useMemo(() => buildInitialValues(fields), [fields])
  const toPayload = useMemo(() => normalizePayload(fields), [fields])

  const toggle = () => setModal(prev => !prev)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(
      async values => {
        try {
          const payload = transformPayload
            ? transformPayload(toPayload(values), values)
            : toPayload(values)

          // name is always sent: the API derives slug from name on update.
          const res = await editImporter({
            id: importer?.id,
            name: importer?.name,
            ...payload,
          })

          handleResponse(res, `${title} updated successfully.`, true)

          if (res?.success) {
            toggle()
            onSaved?.()
          }
        } catch (error) {
          handleError(error, `Failed to update ${title.toLowerCase()}.`)
        }
      },
      [
        editImporter,
        importer?.id,
        importer?.name,
        handleError,
        handleResponse,
        onSaved,
        title,
        toPayload,
        transformPayload,
      ]
    ),
  })
  const { values } = formik

  useEffect(() => {
    if (modal) {
      formik.resetForm({ values: initialValues })
    }
  }, [modal])

  return (
    <CanView access={[ACCESS.VEHICLE_IMPORTER.EDIT_VEHICLE_IMPORTER]}>
      <>
        <ButtonTooltip
          type="edit"
          btnId={btnId}
          btnName={btnName}
          onClick={toggle}
          style={btnStyle}
        />

        <Modal isOpen={modal} toggle={toggle} centered size="lg">
          <ModalHeader toggle={toggle}>{title}</ModalHeader>
          <ModalBody>
            <Form
              onSubmit={e => {
                e.preventDefault()
                formik.handleSubmit()
              }}
            >
              <Row>
                {fields
                  .filter(field => shouldShowField(field, values))
                  .map(field => (
                    <Col
                      md={field.col ?? 12}
                      key={field.name}
                      className="mb-3"
                    >
                      {field.type === "repeater" ? (
                        <RepeaterField
                          field={field}
                          values={formik.values[field.name]}
                          setFieldValue={formik.setFieldValue}
                        />
                      ) : field.type === "creatable" ? (
                        <FormikCreatableSelect
                          name={field.name}
                          label={field.label}
                          formik={formik}
                          options={field.options || []}
                          placeholder={
                            field.placeholder ||
                            `Select or type ${field.label}`
                          }
                          loading={field.loading}
                          required={field.required}
                          isCreatable
                          onCreateOption={async inputValue => {
                            if (field.onCreateOption) {
                              const newValue = await field.onCreateOption(
                                inputValue
                              )
                              if (newValue !== undefined) {
                                formik.setFieldValue(
                                  field.name,
                                  String(newValue)
                                )
                              }
                            }
                          }}
                        />
                      ) : field.type === "select" ? (
                        <>
                          <Label htmlFor={field.name}>
                            {field.label}
                            {field.required ? (
                              <span className="text-danger"> *</span>
                            ) : null}
                            {getOptionLabel(
                              field.options,
                              formik.values[field.name]
                            ) ? (
                              <span className="ms-2 text-muted fw-normal">
                                {getOptionLabel(
                                  field.options,
                                  formik.values[field.name]
                                )}
                              </span>
                            ) : null}
                          </Label>
                          <Input
                            id={field.name}
                            name={field.name}
                            type="select"
                            value={
                              formik.values[field.name] === null ||
                              formik.values[field.name] === undefined
                                ? ""
                                : String(formik.values[field.name])
                            }
                            onChange={formik.handleChange}
                            invalid={!!formik.errors[field.name]}
                            disabled={field.loading}
                          >
                            <option value="">
                              {field.placeholder || `Select ${field.label}`}
                            </option>
                            {(field.options || []).map(opt => {
                              const optionValue =
                                opt?.value === null ||
                                opt?.value === undefined
                                  ? ""
                                  : String(opt.value)

                              return (
                                <option key={optionValue} value={optionValue}>
                                  {opt.label}
                                </option>
                              )
                            })}
                          </Input>
                          {field.helpText ? (
                            <small className="text-muted d-block mt-1">
                              {field.helpText}
                            </small>
                          ) : null}
                          {formik.errors[field.name] ? (
                            <FormFeedback className="d-block">
                              {formik.errors[field.name]}
                            </FormFeedback>
                          ) : null}
                        </>
                      ) : field.type === "checkbox" ? (
                        <div className="form-check form-switch mt-4 pt-2">
                          <Input
                            type="switch"
                            id={field.name}
                            name={field.name}
                            checked={formik.values[field.name]}
                            onChange={formik.handleChange}
                          />
                          <Label
                            htmlFor={field.name}
                            className="form-check-label"
                          >
                            {field.label}
                          </Label>
                        </div>
                      ) : (
                        <>
                          <Label htmlFor={field.name}>
                            {field.label}
                            {field.required ? (
                              <span className="text-danger"> *</span>
                            ) : null}
                          </Label>
                          <Input
                            id={field.name}
                            name={field.name}
                            type={
                              field.type === "json" ? "textarea" : field.type
                            }
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            invalid={!!formik.errors[field.name]}
                            rows={
                              field.type === "json" ||
                              field.type === "textarea"
                                ? field.rows || 5
                                : undefined
                            }
                          />
                          {field.helpText ? (
                            <small className="text-muted d-block mt-1">
                              {field.helpText}
                            </small>
                          ) : null}
                          {formik.errors[field.name] ? (
                            <FormFeedback className="d-block">
                              {formik.errors[field.name]}
                            </FormFeedback>
                          ) : null}
                        </>
                      )}
                    </Col>
                  ))}
              </Row>

              <div className="text-end mt-4">
                <Button
                  color="secondary"
                  outline
                  type="button"
                  onClick={toggle}
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button color="success" type="submit" disabled={isPending}>
                  Save Changes{isPending ? "..." : ""}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </>
    </CanView>
  )
}
